import { useEffect, useState } from "react";
import { Add, Delete, Edit } from "@mui/icons-material";
import { Alert, Box, Dialog, Divider, Stack, Tooltip, Typography } from "@mui/material";

import messages from "../../../constants/messages";
import { useRuleWizardContext } from "../../../providers/RuleWizardProvider";
import { CoAppTextButton } from "../../global/styled/global.styled";
import { RuleSidebarRow, RuleSidebarRowActions, RuleTriggerSidebarContainer
} from "../styled/rule-sidebars.styled";

import TriggerDialog from "./trigger-dialog";

export default function RuleTriggerSidebar(props) {
	const { editedId, editedVersionOfRule, triggers, formula, handleSaveChanges, setStepDataHasChanged, setTriggers } = useRuleWizardContext();

	/**
	 * Location previously selected
	 * Triggers should be associated with a location
	 */
	const location = props.location;

	/**
	 * Trigger Object Fields, based on user input
	 */
	const [triggerField, setTriggerField] = useState({});
	const [triggerName, setTriggerName] = useState("");
	const instanceType = "currentInstance";
	const [isEditing, setIsEditing] = useState(false);
	const [showTriggerType, setShowTriggerType] = useState(false);
	const [clickAction, setClickAction] = useState(false);

	/**
	 * Trigger field rule error state.
	 */
	const [triggerFieldValueError, setTriggerFieldValueError] = useState("");
	const [triggerNameValueError, setTriggerNameValueError] = useState("");
	const [triggerTypeValueError, setTriggerTypeValueError] = useState("");
	const [formHasErrors, setFormHasErrors] = useState(false);
	const [noLocationSelectedError, setNoLocationSelectedError] = useState("");

	/**
	 * Triggers data manipulation
	 */
	const [editTriggerIndex, setEditTriggerIndex] = useState("");

	/**
	 * New/Edit Trigger - Dialog Handling
	 */
	const [open, setOpen] = useState(false);

	/**
	 * Validate trigger dialog field before allowing user to save to redux.
	 * @returns boolean
	 */
	const validateFields = () => {
		let hasErrors = false;
		if (Object.keys(triggerField).length === 0 || triggerField === "") {
			setTriggerFieldValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
			hasErrors = true;
		} else {
			setTriggerFieldValueError("");
		}

		if (triggerName === "") {
			setTriggerNameValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
			hasErrors = true;
		} else {
			setTriggerNameValueError("");
		}

		if (hasErrors) {
			setFormHasErrors(true);
			return false;
		} else {
			return true;
		}
	};

	const clearFields = () => {
		setTriggerField({});
		setTriggerName("");
		setTriggerFieldValueError("");
		setTriggerNameValueError("");
		setClickAction(false);
		setShowTriggerType(false);
	};

	const handleClickOpen = () => {
		if (Object.keys(location).length === 0) {
			setNoLocationSelectedError(messages.TRIGGER_NO_LOCATION_SELECTED_ERROR_MSG);
			return;
		}
		clearFields();
		setOpen(true);
		setIsEditing(false);
	};

	const handleCancel = () => {
		clearFields();
		setOpen(false);
	};

	/**
	 * Edit Dialog handling
	 */
	const handleClickOpenEditDialog = (trigger, index) => {
		props.handleTriggerFieldLoad(trigger.location);
		setTriggerField(trigger.triggerField);
		setTriggerName(trigger.triggerName);
		setEditTriggerIndex(index);
		setIsEditing(true);
		setOpen(true);
		setClickAction(trigger.when === "onClick");
		const triggerField = trigger.triggerField;
		setShowTriggerType(triggerField?.htmlFieldType?.name === "button" || triggerField?.clickAction === true);
	};

	const handleEditSave = async () => {
		if (!validateFields()) {
			return;
		} else {
			const selectedField = props.triggerFields.find(field => field.name === triggerField?.name);
			try {
				let updatedTrigger = {
					id: Math.random(),
					triggerName: triggerName,
					instanceType: instanceType,
					triggerField: { 
						...selectedField,
						locationIsSimplePopup: triggers[editTriggerIndex].location.isSimplePopup,
						locationIsIframe: triggers[editTriggerIndex].location.isIframe,
						locationIFrameName: triggers[editTriggerIndex].location.iFrameName,
						locationIFrameTitle: triggers[editTriggerIndex].location.iFrameTitle,
						locationSimplePopupTitle: triggers[editTriggerIndex].location.simplePopupTitle
					},
					when: clickAction ? "onClick" : "onFieldValue",
					// TODO: need ruleFieldId? it's in the triggerField object
					ruleFieldId: selectedField.id,
					ruleLocationId: triggers[editTriggerIndex].location.id,
					clickAction: clickAction,
					location: { id: triggers[editTriggerIndex].location.id, name: triggers[editTriggerIndex].location.name },
				};

				let nt = [...triggers];
				nt.splice(editTriggerIndex, 1, updatedTrigger);
				handleSaveChanges(nt);
				setEditTriggerIndex("");
				setTriggers(nt);
				setIsEditing(false);
				setOpen(false);

				// TODO: need this?
				if (editedId) {
					setStepDataHasChanged(true);
				}
			} catch (error) {
				console.error("Error during trigger edit:", error);
				setFormHasErrors(true);
			}
		}
	};

	const handleDeleteTrigger = (index) => {
		let triggerIndex = index ? index : editTriggerIndex;
		let nt = [...triggers];
		nt.splice(triggerIndex, 1);
		setEditTriggerIndex("");
		setTriggers(nt);
		setIsEditing(false);
		setOpen(false);
		handleSaveChanges(nt);
	};

	/**
	 * Add new trigger to array of triggers managed by redux
	 */
	const handleSave = () => {
		try {
			const selectedField = props.triggerFields.find(field => field.name === triggerField?.name);
			let newTrigger = {
				clickAction: clickAction,
				id: Math.random(),
				instanceType: instanceType,
				triggerField: {
					...selectedField,
					locationIsSimplePopup: location.isSimplePopup,
					locationIsIframe: location.isIframe,
					locationIFrameName: location.iFrameName,
					locationIFrameTitle: location.iFrameTitle,
					locationSimplePopupTitle: location.simplePopupTitle,
				},
				triggerName: triggerName,
				// TODO: need ruleFieldId? it's in the triggerField object
				ruleFieldId: selectedField.id,
				ruleLocationId: location.id,
				when: clickAction ? "onClick" : "onFieldValue",
				location: { id: location.id, name: location.name },
			};
			let nt = [...triggers, newTrigger];
			handleSaveChanges(nt);

			setTriggers(nt);
			setOpen(false);
			
			// TODO: need this?
			if (editedId) {
				setStepDataHasChanged(true);
			}
		} catch (error) {
			console.error("Error during trigger creation:", error);
			setFormHasErrors(true);
		}
	};

	/**
	 * Individual Field handling in the New and Edit dialogs
	 */
	const handleTriggerNameInput = (e) => {
		setTriggerName(e.target.value);
		if (e.target.value.length === 0) {
			setFormHasErrors(true);
			setTriggerNameValueError(messages.FIELD_IS_REQUIRED_ERROR_MSG);
		} else {
			setFormHasErrors(false);
			setTriggerNameValueError("");
		}
	};

	/**
	 * Field has changed. 
	 * If field has otherInstanceEnabled = true, 
	 * then display instance type options
	 */
	const handleFieldDropdownSelectionChange = (e) => {
		const selectedField = props.triggerFields.find(field => field.name === e.target.value);
		setTriggerField(selectedField);
		setTriggerFieldValueError("");
		setFormHasErrors(false);
		const hasClickAction = selectedField?.htmlFieldType?.name === "button" || selectedField?.clickAction === true;
		setShowTriggerType(hasClickAction);
		if (hasClickAction) {
			setClickAction(true);
		} else {
			setClickAction(false);
		}
		
		// Set default trigger name based on selected field if no name exists or when field changes
		if (triggerName === "" || triggerName === triggerField?.name) {
			setTriggerName(selectedField?.name);
			setTriggerNameValueError("");
		}
	};

	const handleTriggerTypeChange = (e) => {
		setTriggerTypeValueError("");
		setFormHasErrors(false);
		setClickAction(e.target.value);
	};

	const isTriggerInFormula = (trigger) => {
		if (!formula) return false;
		let hasTrigger = false;
		formula?.formula?.forEach(formulaItem => {
			if (formulaItem.triggerField && formulaItem.triggerField.id === trigger.triggerField.id) {
				hasTrigger = true;
			}
		});

		return hasTrigger;
	};

	useEffect(() => {
		if (editedId && Object.keys(editedVersionOfRule).length > 0) {
			if (triggers.length !== editedVersionOfRule.ruletriggers.length) {
				setStepDataHasChanged(true);
			}
		}
	}, [location, triggers]);

	return (
		<div>
			<RuleTriggerSidebarContainer>
				<Box sx={{ display: "flex", justifyContent: "space-between" }}>
					<Typography sx={{ padding: "5px"}} variant="body1">Triggers</Typography>
					<CoAppTextButton onClick={handleClickOpen}><Add/></CoAppTextButton>
				</Box>
				<Divider sx={{ width: "100%" }} />

				<Stack alignItems="flexStart" direction="column" spacing="0.5" sx={{ overflow: "auto" }}>
					{
						triggers.map((trigger, index) =>
							<div key={trigger.id}>
								<RuleSidebarRow>
									<div>
										<Typography sx={{ fontWeight: "600" }} variant="body2">{trigger.triggerName}</Typography>
										<Typography variant="body2">Location: {trigger.location.name}</Typography>
										<Typography variant="body2">Field Name: {trigger.triggerField.name}</Typography>
										<Typography variant="body2">Field Type: {trigger.triggerField?.dataFieldType?.name}</Typography>
										<Typography variant="body2">Trigger Type: {trigger.when === "onClick" ? "On Click" : "Field Value"}</Typography>
									</div>
									<RuleSidebarRowActions alignItems="flexStart" direction="row">
										<Tooltip title={
											isTriggerInFormula(trigger) 
												? "This trigger cannot be edited because it is used in the formula"
												: "Edit trigger"
										}>
											<Edit onClick={() => !isTriggerInFormula(trigger) && handleClickOpenEditDialog(trigger, index)}
												sx={{ 
													fontSize: "medium", 
													color: "#1D38BD",
													opacity: isTriggerInFormula(trigger) ? 0.5 : 1,
													cursor: isTriggerInFormula(trigger) ? "not-allowed" : "pointer"
												}} />
										</Tooltip>
										<Tooltip title={
											isTriggerInFormula(trigger) 
												? "This trigger cannot be deleted because it is used in the formula"
												: "Delete trigger"
										}>
											<span>
												<Delete 
													onClick={() => !isTriggerInFormula(trigger) && handleDeleteTrigger(index)} 
													sx={{ 
														fontSize: "medium", 
														color: "#1D38BD",
														opacity: isTriggerInFormula(trigger) ? 0.5 : 1,
														cursor: isTriggerInFormula(trigger) ? "not-allowed" : "pointer"
													}} 
												/>
											</span>
										</Tooltip>
									</RuleSidebarRowActions>
								</RuleSidebarRow>
								<Divider sx={{ width: "100%" }} />
							</div>
						)
					}
				</Stack>
			</RuleTriggerSidebarContainer>

			<TriggerDialog
				allTriggerFields={props.triggerFields}
				cancelDialogHandler={handleCancel}
				cancelHandler={handleCancel}
				clickAction={clickAction}
				deleteTriggerHandler={handleDeleteTrigger}
				fieldDropdownSelectionChangeHandler={handleFieldDropdownSelectionChange}
				formHasErrors={formHasErrors}
				nameOfTrigger={triggerName}
				nameOfTriggerField={triggerField}
				openDialogFlag={open}
				openAsEditFlag={isEditing}
				saveHandler={handleSave}
				saveEditHandler={handleEditSave}
				showTriggerType={showTriggerType}
				triggerNameInputHandler={handleTriggerNameInput}
				triggerTypeChangeHandler={handleTriggerTypeChange}
				triggerNameValueError={triggerNameValueError}
				triggerFieldValueError={triggerFieldValueError}
				triggerTypeValueError={triggerTypeValueError}
			/>

			<Dialog onClose={() => setNoLocationSelectedError("")} open={noLocationSelectedError !== ""}>
				<Alert severity="error">{noLocationSelectedError}</Alert>
			</Dialog>
		</div>
	);
}